import { useEffect, useMemo, useState } from 'react';
import { ScopeEditor, type Area, type Level } from '../components/mcp/McpConnectionsView';

// Tela de consentimento do OAuth do MCP do IntegraIA (ex.: conector do ChatGPT). O backend manda
// o navegador para cá (/oauth/mcp?client_id=...&redirect_uri=...&code_challenge=...&state=...);
// o usuário escolhe as áreas liberadas e "Permitir" cria a conexão (chave MCP) e volta para o
// cliente com o código. A conexão aparece em Business Core > MCP.

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export function McpOAuthConsentPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [client, setClient] = useState<{ name: string; redirectHost: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoApprove, setAutoApprove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [areas, setAreas] = useState<Area[]>([]);
  const [scopes, setScopes] = useState<Record<string, Level>>({});

  useEffect(() => {
    fetch('/api/mcp-connections/areas', { headers: headers() }).then(r => r.json()).then(j => {
      const list: Area[] = j?.data || [];
      setAreas(list);
      setScopes(Object.fromEntries(list.map(a => [a.key, 'read' as Level])));
    }).catch(() => null);
    const q = new URLSearchParams({ client_id: params.get('client_id') || '', redirect_uri: params.get('redirect_uri') || '' });
    fetch(`/api/mcp-connections/oauth/client?${q}`, { headers: headers() })
      .then(r => r.json())
      .then(j => (j?.success ? setClient(j.data) : setError(j?.message || 'Pedido de conexão inválido.')))
      .catch(() => setError('Não foi possível carregar o pedido de conexão.'));
  }, [params]);

  const decide = async (decision: 'allow' | 'deny') => {
    if (decision === 'allow' && areas.length && !Object.values(scopes).some(l => l !== 'off')) { setError('Libere pelo menos uma área.'); return; }
    if (decision === 'allow' && autoApprove && !confirm('Com a aprovação automática, alterações pedidas por este app (inclusive o que gasta créditos) são aplicadas sem passar por você. Continuar?')) return;
    setBusy(true);
    try {
      const body: Record<string, any> = { decision, autoApprove, ...(areas.length ? { scopes } : {}) };
      for (const k of ['client_id', 'redirect_uri', 'code_challenge', 'code_challenge_method', 'state', 'resource']) body[k] = params.get(k);
      const res = await fetch('/api/mcp-connections/oauth/decide', { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      const j = await res.json();
      if (!j?.success) throw new Error(j?.message || 'Não foi possível concluir.');
      window.location.href = j.data.redirect;
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">IntegraIA · MCP</p>
        {error ? (
          <>
            <h1 className="mt-2 text-lg font-bold text-slate-900">Não deu para conectar</h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
          </>
        ) : !client ? (
          <p className="mt-4 text-sm text-slate-500">Carregando…</p>
        ) : (
          <>
            <h1 className="mt-2 text-lg font-bold text-slate-900">{client.name} quer acessar o IntegraIA da sua conta</h1>
            <p className="mt-2 text-sm text-slate-600">Escolha o que ele pode ler e alterar. Alterações viram pedidos para você aprovar em Business Core › MCP (a não ser que ligue a aprovação automática).</p>
            <p className="mt-1 text-[11px] text-slate-400">Volta para: {client.redirectHost}</p>
            {areas.length > 0 && <div className="mt-4"><ScopeEditor areas={areas} value={scopes} onChange={setScopes} /></div>}
            <label className={`mt-4 flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 ${autoApprove ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
              <input type="checkbox" checked={autoApprove} onChange={e => setAutoApprove(e.target.checked)} className="mt-0.5 h-4 w-4" />
              <span>
                <span className="block text-xs font-semibold text-slate-800">Aprovar automaticamente as alterações?</span>
                <span className="block text-[11px] text-slate-500">Ligado: o que o app pedir é aplicado na hora (as travas continuam valendo). Desligado: cada alteração vira um pedido para você aprovar em Business Core › MCP.</span>
              </span>
            </label>
            <div className="mt-5 flex gap-2">
              <button type="button" disabled={busy} onClick={() => decide('allow')} className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Permitir</button>
              <button type="button" disabled={busy} onClick={() => decide('deny')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">Negar</button>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">Dá para mudar as permissões ou revogar quando quiser em Business Core › MCP.</p>
          </>
        )}
      </div>
    </div>
  );
}
